import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PrimaryButton from '../components/PrimaryButton';
import { setCurrentStep, updateFormData } from '../features/FormRegistration';
import InfoIcon from '../icons/InfoIcon';
import { CoverIsFor, CoverType } from '../types/types';
import MultiStepFormLayout from '../Layout/FormLayout';
import { pushDataLayer } from '../utils/helpers';
import SelectableOptions from '../components/SelectableOptions';
import FeatureSelectionHospital from '../components/FeatureSelectionHospital';
import FeatureSelectionExtras from '../components/FeatureSelectionExtras';
import SecondaryButton from '../components/SecondaryButton';
import LeftChevron from '../icons/LeftChevron';

function Step3() {
  const dispatch = useDispatch();
  const { formData } = useSelector((state: any) => state.formRegistration);
  const { isMobileView } = useSelector((state: any) => state.viewport);

  useEffect(() => {
    if (formData.typeOfCover === CoverType.Hospital) {
      dispatch(
        updateFormData({
          ...formData,
          selectedExtrasBenefits: [],
        }),
      );
    } else if (formData.typeOfCover === CoverType.Extras) {
      dispatch(
        updateFormData({
          ...formData,
          selectedHospitalBenefits: [],
        }),
      );
    }
  }, [formData.typeOfCover]);

  // ✅ Keep validation function
  const isFormValid = () => {
    if (formData.typeOfCover === '-') return false;

    if (formData.typeOfCover === CoverType.HospitalAndExtras) {
      return (
        formData.selectedHospitalBenefits &&
        formData.selectedHospitalBenefits.length > 0 &&
        formData.selectedExtrasBenefits &&
        formData.selectedExtrasBenefits.length > 0
      );
    }

    if (formData.typeOfCover === CoverType.Hospital) {
      return (
        formData.selectedHospitalBenefits &&
        formData.selectedHospitalBenefits.length > 0
      );
    }

    if (formData.typeOfCover === CoverType.Extras) {
      return formData.selectedExtrasBenefits && formData.selectedExtrasBenefits.length > 0;
    }

    return true;
  };

  if (isMobileView) {
    return (
      <MultiStepFormLayout>
        <div className="flex flex-col items-center h-full justify-between">
          <h1 className="text-[22px] text-start font-bold self-start">
            What cover would you like?
          </h1>
          <SelectableOptions
            label="Please choose your preferred cover type"
            WithAsterisk
            options={[
              CoverType.HospitalAndExtras,
              CoverType.Hospital,
              CoverType.Extras,
            ]}
            optionClassname="w-full"
            value={formData.typeOfCover}
            onChange={(value) => {
              dispatch(
                updateFormData({
                  ...formData,
                  typeOfCover: value,
                }),
              );
            }}
          />
          
          {/* ✅ ORIGINAL LAYOUT: Each section in its own conditional block */}
          {formData.typeOfCover === CoverType.Hospital && (
            <FeatureSelectionHospital />
          )}

          {formData.typeOfCover === CoverType.Extras && (
            <FeatureSelectionExtras />
          )}

          {formData.typeOfCover === CoverType.HospitalAndExtras && (
            <>
              <FeatureSelectionHospital />
              <FeatureSelectionExtras />
            </>
          )}

          {/* ✅ ADD: Validation message only when needed - positioned separately */}
          {formData.typeOfCover === CoverType.HospitalAndExtras &&
            (!formData.selectedHospitalBenefits?.length ||
              !formData.selectedExtrasBenefits?.length) && (
              <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded border border-red-200 w-full">
                Please select at least one option from both Hospital and Extras covers to continue
              </div>
            )}

          <div className="flex flex-col items-start text-start gap-[15px] w-full md:w-[560px] mb-[50px] md:mb-[auto]">
            <InfoIcon style={{ width: 14 }} />
            <p className="font-normal text-[12px]">
              Please note that Health Deal does not represent all health funds
              in Australia, nor do we offer every policy available from our
              partner health funds. Our aim is to provide a tailored comparison
              based on the policies we have access to through our panel to find
              you a better deal.
            </p>
          </div>
          <div className="w-full justify-between h-full flex  items-center gap-[10px] sticky bottom-0 bg-[white] p-4 z-10 border-t border-[#D1D7DF] mt-6">
            <SecondaryButton
              leftSection={<LeftChevron />}
              onClick={() => {
                if (
                  formData.coverIsFor === CoverIsFor.Single ||
                  formData.coverIsFor === CoverIsFor.Couple
                ) {
                  dispatch(setCurrentStep(2));
                } else {
                  dispatch(setCurrentStep(3));
                }
              }}>
              Previous
            </SecondaryButton>
            <PrimaryButton
              disabled={!isFormValid()}
              text="Continue"
              classNames="w-[50%] md:w-auto"
              onClick={() => {
                pushDataLayer('funnel_hospital_extras', {
                  cover_type: formData.typeOfCover,
                });
                dispatch(setCurrentStep(6));
              }}
            />
          </div>
        </div>
      </MultiStepFormLayout>
    );
  }

  return (
    <MultiStepFormLayout>
      <div className="flex flex-col items-center h-full justify-between gap-[20px]">
        <h1 className="text-[22px] text-start font-bold self-start">
          What cover would you like?
        </h1>
        <SelectableOptions
          label="Please choose your preferred cover type"
          WithAsterisk
          options={[
            CoverType.HospitalAndExtras,
            CoverType.Hospital,
            CoverType.Extras,
          ]}
          optionClassname="-full"
          value={formData.typeOfCover}
          onChange={(value) => {
            dispatch(
              updateFormData({
                ...formData,
                typeOfCover: value,
              }),
            );
          }}
        />
        
        {/* ✅ ORIGINAL LAYOUT: Each section in its own conditional block */}
        {formData.typeOfCover === CoverType.Hospital && (
          <FeatureSelectionHospital />
        )}

        {formData.typeOfCover === CoverType.Extras && (
          <FeatureSelectionExtras />
        )}

        {formData.typeOfCover === CoverType.HospitalAndExtras && (
          <div className="flex flex-col gap-[35px] w-full">
            <FeatureSelectionHospital />
            <FeatureSelectionExtras />
          </div>
        )}

        {/* ✅ ADD: Validation message only when needed - positioned separately */}
        {formData.typeOfCover === CoverType.HospitalAndExtras &&
          (!formData.selectedHospitalBenefits?.length ||
            !formData.selectedExtrasBenefits?.length) && (
            <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded border border-red-200 w-full">
              Please select at least one option from both Hospital and Extras covers to continue
            </div>
          )}

        <div className="flex flex-col items-start text-start gap-[15px] w-full mb-[50px] md:mb-[auto]">
          <InfoIcon style={{ width: 14 }} />
          <p className="font-normal text-[12px]">
            Please note that Health Deal does not represent all health funds in
            Australia, nor do we offer every policy available from our partner
            health funds. Our aim is to provide a tailored comparison based on
            the policies we have access to through our panel to find you a
            better deal.
          </p>
        </div>
        <div className="w-full justify-between h-full flex  items-center gap-[10px] sticky bottom-0 bg-[white] p-4 z-10 border-t border-[#D1D7DF] mt-6">
          <SecondaryButton
            leftSection={<LeftChevron />}
            onClick={() => {
              if (
                formData.coverIsFor === CoverIsFor.Single ||
                formData.coverIsFor === CoverIsFor.Couple
              ) {
                dispatch(setCurrentStep(2));
              } else {
                dispatch(setCurrentStep(3));
              }
            }}>
            Previous
          </SecondaryButton>
          <PrimaryButton
            disabled={!isFormValid()}
            text="Continue"
            classNames="w-[50%] md:w-auto"
            onClick={() => {
              pushDataLayer('funnel_hospital_extras', {
                cover_type: formData.typeOfCover,
              });
              dispatch(setCurrentStep(6));
            }}
          />
        </div>
      </div>
    </MultiStepFormLayout>
  );
}

export default Step3;
